import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drag-to-resize for the left "My Map" / Layers sidebar.
 *
 * Mirrors `useFixedBottomDrawerResize` but for a LEFT-anchored panel: the drag
 * handle lives on the panel's RIGHT edge, so dragging right widens it
 * (delta = clientX - startX). Width is in px, persisted to localStorage, and
 * clamped to a sensible min and a viewport-relative max.
 */
const STORAGE_KEY = 'atlas.layersPanelWidthPx';
/** Default expanded width — keep in sync with LAYER_PANEL_EXPANDED_WIDTH. */
const DEFAULT_PX = 344;
const MIN_PX = 288;
const MAX_PX = 640;

function clampMax(viewportWidth: number): number {
  // Never let the sidebar swallow more than ~55% of the viewport.
  return Math.min(MAX_PX, Math.max(MIN_PX, Math.round(viewportWidth * 0.55)));
}

export function useLeftSidebarResize() {
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1400
  );

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const minPx = MIN_PX;
  const maxPx = clampMax(viewportW);

  const [widthPx, setWidthPx] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_PX;
    const mx = clampMax(window.innerWidth);
    const persistedRaw = window.localStorage.getItem(STORAGE_KEY);
    const persisted = persistedRaw ? Number(persistedRaw) : NaN;
    const target = Number.isFinite(persisted) ? Math.round(persisted) : DEFAULT_PX;
    return Math.max(MIN_PX, Math.min(mx, target));
  });

  // Keep width within bounds when viewport/limits change.
  useEffect(() => {
    setWidthPx((prev) => Math.max(minPx, Math.min(maxPx, prev)));
  }, [minPx, maxPx]);

  const [isResizing, setIsResizing] = useState(false);
  const [isTouchResizing, setIsTouchResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(widthPx);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = widthPx;
    },
    [widthPx]
  );

  const handleResizeTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      setIsTouchResizing(true);
      resizeStartX.current = e.touches[0].clientX;
      resizeStartWidth.current = widthPx;
    },
    [widthPx]
  );

  const nudgeWidth = useCallback(
    (deltaPx: number) => {
      setWidthPx((prev) => Math.max(minPx, Math.min(maxPx, prev + deltaPx)));
    },
    [minPx, maxPx]
  );

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current; // drag right => wider
      setWidthPx(Math.max(minPx, Math.min(maxPx, resizeStartWidth.current + delta)));
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing, minPx, maxPx]);

  useEffect(() => {
    if (!isTouchResizing) return;
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const delta = e.touches[0].clientX - resizeStartX.current;
      setWidthPx(Math.max(minPx, Math.min(maxPx, resizeStartWidth.current + delta)));
    };
    const onTouchEnd = () => setIsTouchResizing(false);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isTouchResizing, minPx, maxPx]);

  // While dragging, lock the page cursor and suppress text selection.
  useEffect(() => {
    const active = isResizing || isTouchResizing;
    if (!active || typeof document === 'undefined') return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing, isTouchResizing]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, String(widthPx));
  }, [widthPx]);

  return {
    widthPx,
    isResizing: isResizing || isTouchResizing,
    handleResizeMouseDown,
    handleResizeTouchStart,
    nudgeWidth,
  };
}

export const LEFT_SIDEBAR_RESIZE_HANDLE_WIDTH = 6;
